import React from 'react';
import { CheckCircle2, Circle, Zap, AlertTriangle, History } from 'lucide-react';
import TrackButton from './TrackButton';
import RadarPrep from './RadarPrep';

const fmtPrice = (v) => {
    if (v === null || v === undefined) return '—';
    if (v >= 1000) return Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 });
    if (v >= 1) return Number(v).toLocaleString('en-US', { maximumFractionDigits: 4 });
    return Number(v).toLocaleString('en-US', { maximumFractionDigits: 8 });
};

export const stageStyle = (stage, direction) => {
    if (stage === 'AC') {
        return direction === 'short'
            ? { wrap: 'border-rose-400/60 bg-rose-950/30', badge: 'bg-rose-500 text-white', seg: 'bg-rose-400' }
            : { wrap: 'border-emerald-400/60 bg-emerald-950/30', badge: 'bg-emerald-500 text-slate-950', seg: 'bg-emerald-400' };
    }
    if (stage === 'HAZIRLAN') return { wrap: 'border-amber-400/40 bg-slate-900/70', badge: 'bg-amber-400/20 text-amber-100 border border-amber-400/50', seg: 'bg-amber-400' };
    return { wrap: 'border-sky-500/30 bg-slate-900/60', badge: 'bg-sky-500/15 text-sky-200 border border-sky-500/40', seg: 'bg-sky-400' };
};

export const stageText = (rd) => {
    const dir = rd.direction === 'long' ? 'LONG' : rd.direction === 'short' ? 'SHORT' : '';
    if (rd.stage === 'AC') return `POZİSYON AÇ · ${dir}`;
    if (rd.stage === 'HAZIRLAN') return dir ? `${dir}'A HAZIRLAN` : 'HAZIRLAN · YÖN BELİRSİZ';
    return dir ? `İZLE · ${dir} İZİ` : 'İZLE · İŞLEM YOK';
};

const ConfirmBar = ({ passed, total, seg }) => (
    <div className="flex gap-0.5" aria-label={`${passed}/${total} teyit`}>
        {Array.from({ length: total }).map((_, i) => (
            <div key={`seg-${i}`} className={`h-1.5 flex-1 rounded-sm ${i < passed ? seg : 'bg-slate-700'}`} />
        ))}
    </div>
);

const weightCls = (h) =>
    h.label_tr === 'tutarlı' ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'
        : h.label_tr === 'zayıf' ? 'text-rose-300 border-rose-500/40 bg-rose-500/10'
            : 'text-slate-300 border-slate-600 bg-slate-800/60';

const CheckWeight = ({ c, symbol }) => {
    const h = c.history;
    if (!h || h.n < 5 || h.rate_pct === null || h.rate_pct === undefined) return null;
    return (
        <span
            data-testid={`radar-check-weight-${symbol}-${c.key}`}
            title={`Bu teyit geçtiğinde geçmişte ${h.realized}/${h.n} sinyal tuttu (%${h.rate_pct})${h.label_tr ? ` — ${h.label_tr} kanıt` : ''}`}
            className={`ml-1.5 inline-flex items-center gap-1 rounded-sm border px-1 py-px font-mono text-[9px] leading-none ${c.passed ? weightCls(h) : 'text-slate-500 border-slate-700'}`}
        >
            %{Math.round(h.rate_pct)}
            {h.label_tr && c.passed && <span className="font-sans not-italic">{h.label_tr}</span>}
        </span>
    );
};

const Checklist = ({ checks, symbol }) => (
    <ul className="space-y-1" data-testid={`radar-checklist-${symbol}`}>
        {checks.map((c) => (
            <li key={c.key} className="flex items-start gap-1.5 text-[11px] leading-tight">
                {c.passed ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" /> : <Circle className="h-3.5 w-3.5 text-slate-600 shrink-0" />}
                <span className={c.passed ? 'text-slate-100' : 'text-slate-500'}>
                    {c.label}
                    {c.key === 'breakout' && <span className="text-[9px] text-amber-300 ml-1">(zorunlu)</span>}
                    <CheckWeight c={c} symbol={symbol} />
                    <span className="block text-[10px] text-slate-500">{c.detail}</span>
                </span>
            </li>
        ))}
    </ul>
);

const rateCls = (r) => (r >= 55 ? 'text-emerald-300' : r >= 45 ? 'text-amber-300' : 'text-rose-300');

const SetupHistory = ({ sh, symbol }) => {
    if (!sh) return null;
    return (
        <div data-testid={`radar-setup-history-${symbol}`} className="flex items-start gap-1.5 rounded-md border border-slate-700 bg-slate-900/70 px-2 py-1.5 text-[11px] leading-snug">
            <History className="h-3.5 w-3.5 shrink-0 mt-px text-sky-300" />
            <span className="text-slate-300">
                <span className="text-slate-400">Bu kurulum geçmişte: </span>
                {sh.enough ? (
                    <>
                        <b className={`font-mono ${rateCls(sh.rate_pct)}`}>%{Math.round(sh.rate_pct)} tuttu</b>
                        <span className="text-slate-500"> ({sh.realized}/{sh.n} · {sh.basis_tr})</span>
                    </>
                ) : (
                    <span className="text-slate-400">yeterli geçmiş yok ({sh.n || 0} sinyal) — temkinli ol</span>
                )}
            </span>
        </div>
    );
};

const Levels = ({ rec }) => (
    <div className="grid grid-cols-4 gap-1.5 text-[10px] bg-slate-900/70 rounded-md p-2">
        {[['Giriş', rec.entry, 'text-slate-100'], ['TP1', rec.tp1, 'text-emerald-300'], ['TP2', rec.tp2, 'text-emerald-300'], ['Stop', rec.stop_loss, 'text-rose-300']].map(([l, v, cls]) => (
            <div key={l}>
                <div className="text-[9px] uppercase tracking-wider text-slate-400">{l}</div>
                <div className={`font-mono ${cls}`}>${fmtPrice(v)}</div>
            </div>
        ))}
    </div>
);

export default function RadarCard({ row, history, coinHistory, trackedIds }) {
    const rd = row.radar;
    const st = stageStyle(rd.stage, rd.direction);
    const rec = row.recommendation || {};
    const h = history || rd.history || {};
    return (
        <div data-testid={`radar-card-${row.symbol}`} className={`border rounded-lg p-3 flex flex-col gap-2.5 ${st.wrap}`}>
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                    <Zap className="h-3.5 w-3.5 text-sky-400" />
                    <span className="font-mono font-semibold text-slate-50 text-sm">{row.symbol.replace('USDT', '')}</span>
                </div>
                <span data-testid={`radar-stage-${row.symbol}`} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${st.badge} ${rd.stage === 'AC' ? 'animate-pulse' : ''}`}>
                    {stageText(rd)}
                </span>
            </div>
            <div>
                <div className="flex items-center justify-between text-[10px] mb-1">
                    <span className="text-slate-400">Teyit</span>
                    <span data-testid={`radar-count-${row.symbol}`} className="font-mono font-bold text-slate-100">
                        {rd.passed_count}/{rd.total_checks}
                        {rd.weak_keys?.length > 0 && (
                            <span
                                data-testid={`radar-score-${row.symbol}`}
                                title={`Geçmişte zayıf kalan teyit(ler) yarım sayıldı: ${rd.weak_keys.length} teyit × 0.5`}
                                className="ml-1.5 font-normal text-amber-300"
                            >
                                · ağırlıklı {rd.score}
                            </span>
                        )}
                    </span>
                </div>
                <ConfirmBar passed={rd.passed_count} total={rd.total_checks} seg={st.seg} />
            </div>
            <Checklist checks={rd.checks} symbol={row.symbol} />
            <SetupHistory sh={rd.setup_history} symbol={row.symbol} />
            <div className="text-[11px] text-slate-100 bg-slate-800/80 border border-slate-700 rounded-md px-2 py-1.5 leading-snug">
                <span className="font-semibold text-sky-300">Ne yapmalıyım? </span>{rd.action_tr}
            </div>
            {rd.stage === 'AC' ? (
                <>
                    <Levels rec={rec} />
                    <div className="flex items-center justify-between gap-2 text-[10px]">
                        <span className="text-slate-300">R/R <b className="font-mono">{rec.rr_ratio}</b> · güven %{rec.confidence} · <b data-testid={`radar-leverage-${row.symbol}`}>{rd.leverage?.label || rec.leverage?.label}</b></span>
                        {rd.short_id && <TrackButton shortId={rd.short_id} tracked={trackedIds?.has(rd.short_id)} defaultLeverage={rd.leverage?.value || rec.leverage?.value || 1} testid={`radar-track-${row.symbol}`} />}
                    </div>
                </>
            ) : (
                <>
                    <RadarPrep rd={rd} symbol={row.symbol} />
                    {rd.blockers?.length > 0 && (
                        <div className="text-[10px] text-amber-200/90 leading-snug" data-testid={`radar-blockers-${row.symbol}`}>
                            <AlertTriangle className="h-3 w-3 inline mr-1 -mt-0.5" />
                            POZİSYON AÇ için eksik: {rd.blockers.slice(0, 3).join(' · ')}
                        </div>
                    )}
                </>
            )}
            <div className="mt-auto pt-2 border-t border-slate-700/60 text-[10px] text-slate-400" data-testid={`radar-history-${row.symbol}`}>
                Geçmiş gerçekleşme (bu aşama):{' '}
                {h.rate_pct !== null && h.rate_pct !== undefined && h.n >= 5 ? (
                    <b className="text-slate-100 font-mono">%{h.rate_pct} <span className="text-slate-500 font-normal">({h.n} sinyal)</span></b>
                ) : (
                    <span className="text-slate-300">veri toplanıyor ({h.n || 0} sinyal)</span>
                )}
                <span className="block mt-0.5" data-testid={`radar-coin-history-${row.symbol}`}>
                    Bu coinde:{' '}
                    {coinHistory?.n ? (
                        <b className={`font-mono ${coinHistory.rate_pct >= 50 ? 'text-emerald-300' : 'text-amber-300'}`}>
                            {coinHistory.realized}/{coinHistory.n} tuttu <span className="text-slate-500 font-normal">(%{coinHistory.rate_pct})</span>
                        </b>
                    ) : (
                        <span className="text-slate-500">henüz sonuçlanan sinyal yok</span>
                    )}
                </span>
            </div>
        </div>
    );
}
